import React, { useMemo, useState } from 'react';
import { useGetMany, useGetManyReference, useNotify, useRefresh, useUpdate } from 'react-admin';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getToken } from '../authentication/authProvider';
import { BelongingsContact, Client, Funeral } from '../types';
import {
  BELONGINGS_METHOD_LABEL,
  BELONGINGS_PARCEL,
  BelongingsMethod,
  belongingsLabelState,
  contactFromClient,
  formatContactAddress,
  missingContactParts,
  sameContact,
} from './belongingsShippingModel';

const API_BASE = (() => {
  const url = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
  return url.endsWith('/') ? url.slice(0, -1) : url;
})();

const FIELD =
  'h-8 w-full rounded-md border border-input bg-background px-2 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

const OTHER = 'other';

/** Everyone on the case but the deceased, with their address. */
function useCaseClients(record: Funeral): Client[] {
  const { data: links } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: record.id },
    { enabled: !!record.id },
  );
  const ids = useMemo(() => {
    const seen = new Set<number>();
    const out: number[] = [];
    const add = (v: unknown) => {
      const n = Number(v);
      if (!n || seen.has(n) || n === Number(record.deceased_client_id)) return;
      seen.add(n);
      out.push(n);
    };
    add(record.primary_client_id);
    add(record.paying_client_id);
    add(record.authorized_client_id);
    add(record.declarant_client_id);
    for (const l of links ?? []) add(l.client_id);
    return out;
  }, [links, record]);
  const { data } = useGetMany<Client>('clients', { ids }, { enabled: ids.length > 0 });
  return useMemo(
    () => ids.map((id) => (data ?? []).find((c) => Number(c.id) === id)).filter(Boolean) as Client[],
    [data, ids],
  );
}

async function api(path: string, body: unknown) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

async function openLabel(labelUrl: string, thermal: boolean) {
  const token = await getToken();
  const res = await fetch(
    `${API_BASE}/canada-post/label${thermal ? '/thermal' : ''}?url=${encodeURIComponent(labelUrl)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error('Failed to fetch label');
  const blobUrl = URL.createObjectURL(await res.blob());
  window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/**
 * Personal belongings (T-099): mailed or picked up, and to / by whom. Cleo
 * decides here; the drop-off facility's portal shows it, and for mail prints
 * the label Cleo makes here. Lives inside the Drop-off sidebar block.
 */
export function BelongingsShipping({ record }: { record: Funeral }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: saving }] = useUpdate();
  const clients = useCaseClients(record);
  const [editingOther, setEditingOther] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);

  const method = (record.dropoff_belongings_method ?? null) as BelongingsMethod | null;
  const contact = record.dropoff_belongings_contact ?? null;
  const hasLabel = !!record.dropoff_belongings_label_url;
  const labelState = belongingsLabelState(record);

  const save = (data: Partial<Funeral>, done?: string) =>
    update(
      'funerals',
      { id: record.id, data, previousData: record },
      {
        onSuccess: () => {
          if (done) notify(done, { type: 'success' });
          void refresh();
        },
        onError: (e: any) => notify(`Could not save: ${e?.message ?? 'error'}`, { type: 'error' }),
      },
    );

  const setMethod = (next: BelongingsMethod | null) => {
    if (next === method) return;
    if (hasLabel && next !== 'mail') {
      notify('Void the belongings label before changing how they go home', { type: 'warning' });
      return;
    }
    void save({ dropoff_belongings_method: next }, 'Belongings method saved');
  };

  const setContact = (next: BelongingsContact) => {
    if (hasLabel && !sameContact(next, contact)) {
      notify('Void the belongings label before changing the address', { type: 'warning' });
      return;
    }
    void save({ dropoff_belongings_contact: next }, 'Belongings contact saved');
  };

  const selectValue = contact
    ? contact.client_id && clients.some((c) => Number(c.id) === contact.client_id)
      ? String(contact.client_id)
      : OTHER
    : '';

  const onPick = (value: string) => {
    if (value === OTHER) {
      setEditingOther(true);
      return;
    }
    setEditingOther(false);
    const client = clients.find((c) => String(c.id) === value);
    if (client) setContact(contactFromClient(client));
  };

  const voidLabel = async () => {
    if (!window.confirm('Void this belongings label? Canada Post is asked for a refund.')) return;
    try {
      setVoiding(true);
      await api(`/canada-post/funeral/${record.id}/belongings-label/void`, {});
      notify('Belongings label voided', { type: 'success' });
      void refresh();
    } catch (e) {
      notify(`Could not void: ${(e as Error).message}`, { type: 'error' });
    } finally {
      setVoiding(false);
    }
  };

  const missing = missingContactParts(contact);
  const who = method === 'pickup' ? 'Picked up by' : 'Mail to';

  return (
    <div
      className="mt-1 min-w-0 max-w-full overflow-hidden rounded-md border border-border bg-muted/30 p-2.5 space-y-2"
      data-testid="belongings-shipping"
    >
      <div className="space-y-1.5 min-w-0">
        <div className="text-[13px] font-medium">Belongings go home</div>
        <div className="flex w-full rounded-md border border-input overflow-hidden" role="group">
          {([null, 'mail', 'pickup'] as const).map((m) => (
            <button
              key={m ?? 'unset'}
              type="button"
              disabled={saving}
              onClick={() => setMethod(m)}
              className={`flex-1 px-2 py-1 text-[12px] ${
                method === m ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-accent'
              }`}
            >
              {m ? BELONGINGS_METHOD_LABEL[m] : 'Not set'}
            </button>
          ))}
        </div>
      </div>

      {method && (
        <>
          <label className="block text-[12px] text-muted-foreground">
            {who}
            <select
              className={`${FIELD} mt-1`}
              value={editingOther ? OTHER : selectValue}
              disabled={saving}
              onChange={(e) => onPick(e.target.value)}
            >
              <option value="" disabled>
                Choose a person…
              </option>
              {clients.map((c) => {
                const snap = contactFromClient(c);
                const city = snap.city ? ` — ${snap.city}` : ' — no address';
                return (
                  <option key={String(c.id)} value={String(c.id)}>
                    {snap.name}
                    {city}
                  </option>
                );
              })}
              <option value={OTHER}>Other address…</option>
            </select>
          </label>

          {editingOther && (
            <OtherAddressForm
              initial={contact && !contact.client_id ? contact : null}
              busy={saving}
              onCancel={() => setEditingOther(false)}
              onSave={(c) => {
                setEditingOther(false);
                setContact(c);
              }}
            />
          )}

          {contact && !editingOther && (
            <div className="text-[13px] leading-5">
              <div className="font-medium">{contact.name || 'No name'}</div>
              <div className="text-muted-foreground">{formatContactAddress(contact) || 'No address'}</div>
              {contact.phone && <div className="text-muted-foreground">{contact.phone}</div>}
              {missing.length > 0 && (
                <div className="text-amber-700 text-xs">Missing: {missing.join(', ')}</div>
              )}
            </div>
          )}

          {method === 'mail' && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {labelState === 'ready' && record.dropoff_belongings_label_url ? (
                <>
                  <span className="text-[12px] text-emerald-700">
                    Label ready · <span className="font-mono">{record.dropoff_belongings_tracking_number}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      void openLabel(record.dropoff_belongings_label_url as string, true).catch(() =>
                        notify('Error fetching label', { type: 'error' }),
                      )
                    }
                  >
                    4×6
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void openLabel(record.dropoff_belongings_label_url as string, false).catch(() =>
                        notify('Error fetching label', { type: 'error' }),
                      )
                    }
                  >
                    Full page
                  </Button>
                  <Button size="sm" variant="ghost" disabled={voiding} onClick={() => void voidLabel()}>
                    {voiding ? 'Voiding…' : 'Void'}
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-[12px] text-muted-foreground">
                    {labelState === 'needs_address'
                      ? 'Portal shows “Waiting for label” · needs an address'
                      : 'Portal shows “Waiting for label”'}
                  </span>
                  <Button
                    size="sm"
                    disabled={labelState !== 'waiting'}
                    onClick={() => setLabelOpen(true)}
                  >
                    Create label…
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      )}

      {labelOpen && (
        <BelongingsLabelDialog
          record={record}
          onClose={() => setLabelOpen(false)}
          onCreated={() => {
            setLabelOpen(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function OtherAddressForm({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: BelongingsContact | null;
  busy: boolean;
  onSave: (c: BelongingsContact) => void;
  onCancel: () => void;
}) {
  const [c, setC] = useState<BelongingsContact>(
    initial ?? { client_id: null, province: 'ON' },
  );
  const set = (k: keyof BelongingsContact) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setC((prev) => ({ ...prev, [k]: e.target.value }));
  const f = (k: keyof BelongingsContact, placeholder: string, cls = '') => (
    <input
      className={`${FIELD} ${cls}`}
      placeholder={placeholder}
      aria-label={placeholder}
      value={(c[k] as string) ?? ''}
      onChange={set(k)}
    />
  );
  const clean = (): BelongingsContact => {
    const out: BelongingsContact = { client_id: null };
    for (const k of ['name', 'phone', 'civic_number', 'address', 'apartment', 'city', 'province', 'postal_code'] as const) {
      const v = String(c[k] ?? '').trim();
      out[k] = v ? (k === 'postal_code' || k === 'province' ? v.toUpperCase() : v) : null;
    }
    return out;
  };
  return (
    <div className="grid grid-cols-6 gap-1.5">
      {f('name', 'Name', 'col-span-6')}
      {f('civic_number', 'No.', 'col-span-2')}
      {f('address', 'Street', 'col-span-4')}
      {f('apartment', 'Apt / unit', 'col-span-2')}
      {f('city', 'City', 'col-span-4')}
      {f('province', 'Prov.', 'col-span-2')}
      {f('postal_code', 'Postal code', 'col-span-2')}
      {f('phone', 'Phone (optional)', 'col-span-2')}
      <div className="col-span-6 flex justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={busy} onClick={() => onSave(clean())}>
          Save address
        </Button>
      </div>
    </div>
  );
}

interface Rate {
  serviceCode: string;
  serviceName: string;
  price: number;
  deliveryDays?: number;
}

/**
 * Buys the belongings label: mailed from the drop-off facility to the chosen
 * address, returns to Cleo Montreal, envelope-sized, tracked with signature
 * (SO). Cheapest quoted service preselected.
 */
function BelongingsLabelDialog({
  record,
  onClose,
  onCreated,
}: {
  record: Funeral;
  onClose: () => void;
  onCreated: () => void;
}) {
  const notify = useNotify();
  const [dims, setDims] = useState({
    weight: String(BELONGINGS_PARCEL.weight),
    length: String(BELONGINGS_PARCEL.length),
    width: String(BELONGINGS_PARCEL.width),
    height: String(BELONGINGS_PARCEL.height),
  });
  const [rates, setRates] = useState<Rate[] | null>(null);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState<'rates' | 'create' | null>(null);
  const contact = record.dropoff_belongings_contact;

  const parcel = () => ({
    weight: parseFloat(dims.weight),
    length: parseFloat(dims.length),
    width: parseFloat(dims.width),
    height: parseFloat(dims.height),
  });

  const getRates = async () => {
    try {
      setBusy('rates');
      const data = await api(`/canada-post/funeral/${record.id}/belongings-label/rates`, parcel());
      setRates(data.rates || []);
      setSelected(data.rates?.[0]?.serviceCode ?? '');
    } catch (e) {
      notify(`Error fetching rates: ${(e as Error).message}`, { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    try {
      setBusy('create');
      const data = await api(`/canada-post/funeral/${record.id}/belongings-label`, {
        serviceCode: selected,
        ...parcel(),
      });
      notify(`Belongings label created · ${data.trackingPin}`, { type: 'success' });
      onCreated();
    } catch (e) {
      notify(`Error creating label: ${(e as Error).message}`, { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const dim = (k: keyof typeof dims, label: string) => (
    <label className="text-[12px] text-muted-foreground">
      {label}
      <input
        className={`${FIELD} mt-1`}
        inputMode="decimal"
        value={dims[k]}
        onChange={(e) => {
          setDims((d) => ({ ...d, [k]: e.target.value }));
          setRates(null);
        }}
      />
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Belongings label</DialogTitle>
          <DialogDescription>
            Enters the mail at the drop-off facility, which prints it from its portal. Returns go
            to Cleo Montreal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div>
            <div className="text-muted-foreground text-[12px]">To</div>
            <div className="font-medium">{contact?.name}</div>
            <div>{formatContactAddress(contact)}</div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {dim('weight', 'kg')}
            {dim('length', 'L cm')}
            {dim('width', 'W cm')}
            {dim('height', 'H cm')}
          </div>
          <p className="text-[12px] text-muted-foreground">Envelope or small bag. Tracked, signature on delivery.</p>
          {rates === null ? (
            <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void getRates()}>
              {busy === 'rates' ? 'Getting rates…' : 'Get rates'}
            </Button>
          ) : rates.length === 0 ? (
            <p className="text-amber-700">Canada Post returned no services for this parcel.</p>
          ) : (
            <div className="space-y-1">
              {rates.map((r, i) => (
                <label key={r.serviceCode} className="flex items-center gap-2 rounded border border-input px-2 py-1.5">
                  <input
                    type="radio"
                    name="belongings-rate"
                    checked={selected === r.serviceCode}
                    onChange={() => setSelected(r.serviceCode)}
                  />
                  <span className="flex-1">
                    {r.serviceName}
                    {i === 0 && <span className="ml-1 text-emerald-700 text-[11px]">cheapest</span>}
                    {r.deliveryDays ? (
                      <span className="text-muted-foreground"> · ~{r.deliveryDays} d</span>
                    ) : null}
                  </span>
                  <span className="tabular-nums">${r.price.toFixed(2)}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" disabled={!selected || busy !== null} onClick={() => void create()}>
              {busy === 'create' ? 'Creating…' : 'Create label'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
